/**
 * User Orders Component
 * Displays order history with stats, filtering, and modern card design
 * Follows the unified UX pattern with quick actions and stat widgets
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Calendar,
  CreditCard,
  Download,
  ChevronDown,
  ChevronUp,
  Filter,
  ArrowUpDown,
  ShoppingCart,
  HelpCircle,
} from 'lucide-react';
import { Order, OrderStatus } from '../../types';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import { QuickActionsGrid, QuickAction } from '../common/QuickActionsGrid';

interface UserOrdersProps {
  orders: Order[];
  syncing: boolean;
}

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest';
type FilterOption = 'all' | OrderStatus;

const statusConfig: Record<OrderStatus, { icon: typeof CheckCircle; color: string; bg: string; label: string }> = {
  completed: {
    icon: CheckCircle,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-900/30',
    label: 'Completed',
  },
  processing: {
    icon: RefreshCw,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Processing',
  },
  pending: {
    icon: Clock,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    label: 'Pending',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-100 dark:bg-red-900/30',
    label: 'Failed',
  },
  refunded: {
    icon: RefreshCw,
    color: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-100 dark:bg-gray-700',
    label: 'Refunded',
  },
};

export default function UserOrders({ orders, syncing }: UserOrdersProps): JSX.Element {
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // Calculate order statistics
  const stats = useMemo(() => {
    const completed = orders.filter(o => o.status === 'completed').length;
    const pending = orders.filter(o => o.status === 'pending' || o.status === 'processing').length;
    const totalSpent = orders
      .filter(o => o.status === 'completed')
      .reduce((sum, o) => sum + o.total.amount, 0);

    return { total: orders.length, completed, pending, totalSpent };
  }, [orders]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'total',
      label: 'Total Orders',
      value: stats.total,
      icon: ShoppingBag,
      variant: 'primary',
    },
    {
      id: 'completed',
      label: 'Completed',
      value: stats.completed,
      icon: CheckCircle,
      variant: 'success',
    },
    {
      id: 'pending',
      label: 'In Progress',
      value: stats.pending,
      icon: Clock,
      variant: 'warning',
    },
    {
      id: 'spent',
      label: 'Total Spent',
      value: `$${stats.totalSpent.toFixed(2)}`,
      icon: CreditCard,
      variant: 'default',
    },
  ], [stats]);

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Apply filter
    if (filterBy !== 'all') {
      result = result.filter(o => o.status === filterBy);
    }

    // Apply sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'highest':
          return b.total.amount - a.total.amount;
        case 'lowest':
          return a.total.amount - b.total.amount;
        default:
          return 0;
      }
    });

    return result;
  }, [orders, filterBy, sortBy]);

  // Toggle order expansion
  const toggleOrderExpand = (orderId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  // Format date
  const formatDate = (date: any): string => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  const formatDateTime = (date: any): string => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown';
    }
  };

  // Empty state
  if (orders.length === 0) {
    const emptyActions: QuickAction[] = [
      {
        id: 'browse',
        title: 'Browse Products',
        description: 'Explore our collection of strategy guides',
        icon: 'package',
        linkUrl: '/products',
        buttonText: 'View Products',
        variant: 'primary',
      },
      {
        id: 'help',
        title: 'Need Help?',
        description: 'Learn how to make your first purchase',
        icon: 'help-circle',
        linkUrl: '/help',
        buttonText: 'Help Center',
        variant: 'default',
      },
    ];

    return (
      <div className="space-y-8">
        {/* Syncing Indicator */}
        {syncing && (
          <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <RefreshCw size={14} className="animate-spin mr-2" />
            Syncing your orders...
          </div>
        )}

        {/* Empty State */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
              <ShoppingCart size={48} className="text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            No Orders Yet
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            You haven't placed any orders yet. Start exploring our products to find the perfect strategy guide for you.
          </p>
          <QuickActionsGrid actions={emptyActions} columns={2} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Syncing Indicator */}
      {syncing && (
        <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <RefreshCw size={14} className="animate-spin mr-2" />
          Syncing your orders...
        </div>
      )}

      {/* Stats Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Order Summary
        </h2>
        <StatsWidget stats={statItems} columns={4} />
      </section>

      {/* Filter and Sort Controls */}
      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as FilterOption)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Orders</option>
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <ArrowUpDown size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Amount</option>
              <option value="lowest">Lowest Amount</option>
            </select>
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {filteredOrders.length} of {orders.length} orders
          </div>
        </div>
      </section>

      {/* Orders List */}
      <section className="space-y-4">
        {filteredOrders.map((order) => {
          const status = statusConfig[order.status];
          const StatusIcon = status.icon;
          const isExpanded = expandedOrders.has(order.id);

          return (
            <div
              key={order.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Order Header - Always visible */}
              <div
                className="p-5 cursor-pointer"
                onClick={() => toggleOrderExpand(order.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Order info */}
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    {/* Status Icon */}
                    <div className={`p-2.5 ${status.bg} rounded-lg flex-shrink-0`}>
                      <StatusIcon size={20} className={status.color} />
                    </div>

                    {/* Order Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          Order #{order.id.slice(0, 8).toUpperCase()}
                        </h3>
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color}`}>
                          {status.label}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(order.createdAt)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Package size={14} />
                          {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <CreditCard size={14} />
                          {order.paymentProvider}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Total & Expand */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        ${order.total.amount.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {order.total.currency}
                      </p>
                    </div>
                    <button
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label={isExpanded ? 'Collapse order details' : 'Expand order details'}
                    >
                      {isExpanded ? (
                        <ChevronUp size={20} className="text-gray-500" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-5">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Order Items */}
                    <div className="lg:col-span-2">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Order Items
                      </h4>
                      <div className="space-y-3">
                        {order.items.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                                <Package size={16} className="text-primary-600 dark:text-primary-400" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {item.title || item.productId}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Qty: {item.quantity}
                                </p>
                              </div>
                            </div>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              ${item.price.amount.toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Details */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Order Details
                      </h4>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Order ID</span>
                          <span className="font-mono text-gray-900 dark:text-gray-100">
                            {order.id.slice(0, 12)}...
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Date</span>
                          <span className="text-gray-900 dark:text-gray-100">
                            {formatDateTime(order.createdAt)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Payment</span>
                          <span className="text-gray-900 dark:text-gray-100">
                            {order.paymentProvider}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                          <span className="text-gray-900 dark:text-gray-100">
                            ${order.subtotal.amount.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Total</span>
                          <span className="font-bold text-gray-900 dark:text-gray-100">
                            ${order.total.amount.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      {(order.status === 'completed' || order.status === 'processing') && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <Link
                            to="/user?tab=products"
                            className="btn btn-primary w-full inline-flex items-center justify-center gap-2"
                          >
                            <Download size={16} />
                            View Downloads
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* No Results After Filter */}
      {filteredOrders.length === 0 && orders.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <Filter size={32} className="mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            No orders match your filter. Try adjusting your filters.
          </p>
          <button
            onClick={() => setFilterBy('all')}
            className="mt-4 text-primary-600 dark:text-primary-400 font-medium hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Questions about an order?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Check our help center for FAQs about orders and refunds
              </p>
            </div>
          </div>
          <Link to="/help" className="btn btn-primary whitespace-nowrap">
            Get Help
          </Link>
        </div>
      </section>
    </div>
  );
}
